import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { plans, reassurances, faqItems } from '../../mocks/pricingData';

export default function Join() {
  const [isYearly, setIsYearly] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (id: number) => {
    setOpenFaq(openFaq === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-12 md:pt-44 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(circle at 50% 0%, rgba(59,130,246,0.08), transparent 55%)',
            }}
          />
        </div>
        <div className="relative w-full px-6 md:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-zinc-900 mb-5 leading-tight">
              Pricing
            </h1>
            <p className="text-lg md:text-xl text-zinc-500 leading-relaxed max-w-xl mx-auto">
              Two ways to use LoadAPI.
            </p>
          </div>
        </div>
      </section>

      {/* Toggle */}
      <section className="pb-10 md:pb-14">
        <div className="w-full px-6 md:px-10">
          <div className="max-w-5xl mx-auto flex justify-center">
            <div className="inline-flex items-center bg-zinc-100 rounded-full p-1">
              <button
                type="button"
                onClick={() => setIsYearly(false)}
                className={`px-5 py-2 text-sm font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  !isYearly
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-zinc-500 hover:text-zinc-700'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setIsYearly(true)}
                className={`px-5 py-2 text-sm font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  isYearly
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-zinc-500 hover:text-zinc-700'
                }`}
              >
                Yearly
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Plan Cards */}
      <section className="pb-16 md:pb-24">
        <div className="w-full px-6 md:px-10">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {plans.map((plan) => {
                const showYearly = plan.id === 'single' && isYearly;
                const price = showYearly ? plan.priceYearly : plan.priceMonthly;

                return (
                  <div
                    key={plan.id}
                    className="bg-white rounded-[18px] border border-zinc-200 p-6 md:p-8 flex flex-col"
                    style={{
                      boxShadow:
                        '0 20px 45px -24px rgba(15,23,42,0.35)',
                    }}
                  >
                    {/* Badge */}
                    {plan.badge && (
                      <span className="self-start px-3 py-1 bg-blue-600/10 text-blue-600 text-xs font-semibold rounded-full mb-4">
                        {plan.badge}
                      </span>
                    )}

                    {/* Headline */}
                    <h2 className="text-2xl font-bold text-zinc-900 mb-1">
                      {plan.headline}
                    </h2>
                    <p className="text-sm text-zinc-500 mb-5">
                      {plan.subhead}
                    </p>

                    {/* Price */}
                    <div className="mb-6">
                      <p className="text-3xl font-bold text-zinc-900">
                        {price}
                      </p>
                      {showYearly && plan.yearlyNote && (
                        <p className="text-sm text-zinc-500 mt-1">
                          {plan.yearlyNote} &middot; billed annually
                        </p>
                      )}
                    </div>

                    {/* CTA */}
                    <Link
                      to={plan.ctaLink}
                      className={`block text-center px-6 py-3 text-sm font-semibold rounded-full transition-colors mb-6 whitespace-nowrap ${
                        plan.ctaPrimary
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                      }`}
                    >
                      {plan.ctaText}
                    </Link>

                    {/* Divider */}
                    <div className="border-t border-zinc-200 pt-5 flex-1">
                      <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-3">
                        Included
                      </p>
                      <ul className="space-y-3">
                        {plan.features.map((feature, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-3"
                          >
                            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-check-line text-blue-600"></i>
                            </div>
                            <span className="text-sm text-zinc-600 leading-relaxed">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Reassurance Strip */}
      <section className="pb-16 md:pb-24">
        <div className="w-full px-6 md:px-10">
          <div className="max-w-5xl mx-auto">
            <div className="bg-zinc-50/60 rounded-[18px] border border-zinc-200 p-6 md:p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {reassurances.map((text, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3"
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-shield-check-line text-blue-600"></i>
                    </div>
                    <p className="text-sm text-zinc-600 leading-relaxed">
                      {text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-20 md:pb-28">
        <div className="w-full px-6 md:px-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 mb-8 text-center">
              Frequently asked questions
            </h2>
            <div className="space-y-4">
              {faqItems.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-[18px] border border-zinc-200 overflow-hidden"
                  style={{
                    boxShadow:
                      '0 20px 45px -24px rgba(15,23,42,0.35)',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => toggleFaq(item.id)}
                    className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left cursor-pointer"
                  >
                    <span className="text-base font-semibold text-zinc-900">
                      {item.question}
                    </span>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i
                        className={`${
                          openFaq === item.id
                            ? 'ri-subtract-line'
                            : 'ri-add-line'
                        } text-zinc-400 text-lg`}
                      ></i>
                    </div>
                  </button>
                  {openFaq === item.id && (
                    <div className="px-5 md:px-6 pb-5 md:pb-6">
                      <p className="text-sm text-zinc-500 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(circle at 50% 0%, rgba(59,130,246,0.08), transparent 55%)',
            }}
          />
        </div>
        <div className="relative w-full px-6 md:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-5">
              Ready to stop switching portals?
            </h2>
            <p className="text-zinc-500 text-lg mb-8 max-w-xl mx-auto">
              Start searching loads across four brokers from one place today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="px-7 py-3 bg-blue-600 text-white text-sm font-semibold rounded-full hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                Start now
              </Link>
              <Link
                to="/contact"
                className="px-7 py-3 bg-zinc-100 text-zinc-700 text-sm font-semibold rounded-full hover:bg-zinc-200 transition-colors whitespace-nowrap"
              >
                Talk to sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
